import * as React from "react";
import * as Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import Select from "react-select";
import * as Moment from "moment-timezone";
import uniqBy from "lodash-es/uniqBy";
import { Switch } from "../../forms/components/Switch";
import { useWebPortalApiClient } from "../api/WebPortalTokenClient";
import { TimeZone } from "webportalclient";

export type DateTimeOffsetPickerProps = {
    dateTime: string;
    onChange: (value: Moment.Moment) => void;
    className?: string;
    autoCorrect?: boolean;
};

export const DateTimeOffsetPicker = ({
    dateTime,
    onChange,
    className,
    autoCorrect = true
}: DateTimeOffsetPickerProps) => {
    const [offsetOptions, setOffsetOptions] = React.useState([]);
    const [dateTimeValue, setDateTimeValue] = React.useState(Moment.parseZone(dateTime));
    const [offsetValue, setOffsetValue] = React.useState(Moment.parseZone(dateTime).utcOffset());
    const [initialOffset, _] = React.useState(Moment.parseZone(dateTime).utcOffset());
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const fetchTimeZoneOffsets = async () => {
            const timeZones = await client.client.getTimeZones();

            const offsetOptions: TimeZone[] = uniqBy(timeZones.value, tz => tz.offsetInMinutes)
                .sort((a: TimeZone, b: TimeZone) => a.offsetInMinutes - b.offsetInMinutes)
                .map((tz: TimeZone) => ({
                    label: Moment()
                        .utcOffset(tz.offsetInMinutes)
                        .format("Z"),
                    value: tz.offsetInMinutes
                }));
            setOffsetOptions(offsetOptions);
        };

        fetchTimeZoneOffsets();
    }, []);

    const onChangeHandler = (time, offset) => {
        const removedOffset: Moment.Moment = Moment.utc(Moment(time, !autoCorrect).format("YYYY-MM-DD HH:mm:ss")); // Remove the offset as we are setting it ourselves from another control

        setDateTimeValue(time);
        setOffsetValue(offset);

        onChange(removedOffset.utcOffset(offset, true));
    };

    const vitalographStyles = {
        control: (provided) => ({
            ...provided,
            borderColor: '#e5e5e5',
            minHeight: '32px',
            height: '32px'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#555555'
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 10,
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0px 8px 2px 8px',
        })
    }

    return (
        <div className="row">
            <div className="col-md-3">
            <Datetime
                className={className}
                defaultValue={dateTimeValue}
                onBlur={date => onChangeHandler(date, offsetValue)}
                dateFormat="DD-MMM-YYYY"
                timeFormat="HH:mm:ss"
                />
            </div>
            <div className="col-md-3">
                <Select
                    classNamePrefix="multiselect"
                    styles={vitalographStyles}
                    options={offsetOptions}
                    value={offsetOptions.filter(o => o.value === offsetValue)}
                    getOptionValue={o => o.value}
                    getOptionLabel={o => o.label}
                    onChange={(option) =>
                        onChangeHandler(dateTimeValue, option ? option.value : null)
                    }
                />
            </div>
        </div>
    );
};
